/* ══════════════════════════════════════════════════════════════
   Sector Intelligence — Per-Sector Sentiment + Summary
   ══════════════════════════════════════════════════════════════ */

import { fetchLLMSectorBrief } from '../services/llm-api';

export interface SectorData {
  sector: string;
  sentiment: string;
  sentimentScore: number;
  summary: string;
  topHeadlines: string[];
  keyStocks: Array<{ symbol: string; change: number }>;
  outlook: string;
}

export function renderSectorIntelligence(container: HTMLElement, sectors?: SectorData[]): void {
  if (sectors) {
    renderWithData(container, sectors);
  } else {
    container.innerHTML = `<div style="padding: 2rem; text-align: center;">Loading sector intelligence...</div>`;
    fetchLLMSectorBrief().then(sectorsData => {
      const data = (sectorsData?.length > 0 ? sectorsData : getDefaultSectors()) as SectorData[];
      renderWithData(container, data);
    }).catch(() => {
      renderWithData(container, getDefaultSectors());
    });
  }
}

function getDefaultSectors(): SectorData[] {
  return [
    { sector: 'Banking', sentiment: 'bullish', sentimentScore: 78, summary: 'Surges on RBI dovish hold + HDFC Q3 beat.', topHeadlines: ['HDFC Bank Q3 PAT +18%', 'RBI signals April rate cut'], keyStocks: [{ symbol: 'HDFCBANK', change: 2.3 }, { symbol: 'ICICIBANK', change: 1.8 }, { symbol: 'SBIN', change: 1.5 }], outlook: 'Strong momentum continues' },
    { sector: 'IT', sentiment: 'neutral', sentimentScore: 52, summary: 'Mixed — dollar helps revenue, margin pressure.', topHeadlines: ['TCS/Infosys gain on dollar rally', 'Hiring slowdown continues'], keyStocks: [{ symbol: 'TCS', change: 1.5 }, { symbol: 'INFY', change: 1.2 }, { symbol: 'WIPRO', change: 0.4 }], outlook: 'Selective, large caps preferred' },
    { sector: 'Auto', sentiment: 'bullish', sentimentScore: 70, summary: 'Strong monthly sales + EV momentum.', topHeadlines: ['Maruti +12% YoY retail sales', 'Tata Motors EV sales double'], keyStocks: [{ symbol: 'TATAMOTORS', change: 2.1 }, { symbol: 'MARUTI', change: 1.8 }], outlook: 'Bullish on EV transition' },
    { sector: 'Metals', sentiment: 'bearish', sentimentScore: 35, summary: 'Under pressure — China demand uncertain.', topHeadlines: ['China PMI at 49.8', 'Steel prices fall 5%'], keyStocks: [{ symbol: 'TATASTEEL', change: -1.2 }, { symbol: 'JSWSTEEL', change: -0.9 }], outlook: 'Avoid — wait for China clarity' },
  ];
}

function renderWithData(container: HTMLElement, data: SectorData[]): void {

  const sentimentBar = (score: number) => {
    const color = score >= 60 ? 'var(--india-green)' : score >= 40 ? 'var(--india-saffron)' : 'var(--india-red)';
    return `<div class="sentiment-bar-track"><div class="sentiment-bar-fill" style="width:${score}%; background:${color}"></div></div>`;
  };

  const cards = data.map(s => `
    <div class="sector-intel-card glass-card">
      <div class="sector-header">
        <h4>${s.sector}</h4>
        <span class="sector-score" style="color:${s.sentimentScore >= 60 ? 'var(--india-green)' : s.sentimentScore >= 40 ? 'var(--india-saffron)' : 'var(--india-red)'}">${s.sentimentScore}/100</span>
      </div>
      ${sentimentBar(s.sentimentScore)}
      <p class="sector-summary">${s.summary}</p>
      <div class="sector-stocks">
        ${s.keyStocks.map(st => `<span class="stock-chip ${st.change >= 0 ? 'up' : 'down'}">${st.symbol} ${st.change >= 0 ? '+' : ''}${st.change}%</span>`).join('')}
      </div>
      <div class="sector-headlines">${s.topHeadlines.map(h => `<small>• ${h}</small>`).join('')}</div>
      <p class="sector-outlook">📌 ${s.outlook}</p>
    </div>`).join('');

  container.innerHTML = `<div class="sector-intelligence"><h3>📊 Sector Intelligence</h3><div class="sector-grid">${cards}</div></div>`;
}
